import { useMemo, useState } from 'react';
import { Alert } from '../../core/atoms/Alert';
import { Button } from '../../core/atoms/Button';
import { Group } from '../../core/atoms/Group';
import { Paper } from '../../core/atoms/Paper';
import { Stack } from '../../core/atoms/Stack';
import { Text } from '../../core/atoms/Text';
import { TextInput } from '../../core/atoms/TextInput';
import { Select } from '../../core/atoms/Select';
import { BottomDrawer } from '../../core/molecules/BottomDrawer';
import { ConfirmDialog } from '../../core/molecules/ConfirmDialog';
import { FormField } from '../../core/molecules/FormField';
import { MobileListRow } from '../../core/molecules/MobileListRow';
import { PageHeader } from '../../core/molecules/PageHeader';
import { PhoneInput } from '../../core/molecules/PhoneInput';
import { RowActions } from '../../core/molecules/RowActions';
import { DEFAULT_PAGE_LIMIT } from '../../core/utils/constants';
import {
  detectDefaultPhoneCountry,
  isValidNationalPhone,
  parseStoredPhone,
  type CountryCode,
} from '../../core/utils/phone';
import type { PaginatedResult, ResourceQuery } from '../../core/types';
import { ResourceLayout } from '../TableLayout/ResourceLayout';

/** Row shape every simple contact entity (labour, transport, warehouse…) shares. */
export interface ContactRow {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface ResourceCrudField<T extends ContactRow> {
  /** Key on both the row and the form draft. */
  key: string;
  label: string;
  required?: boolean;
  /** Shown as a table column. Defaults to true. */
  column?: boolean;
  /** Reads the initial form value off an existing row. */
  fromRow?: (row: T) => string;
  /** When set, renders a Select instead of TextInput. */
  options?: Array<{ value: string; label: string }>;
  /** Field control type. Defaults from options / key. */
  type?: 'text' | 'phone' | 'select';
  /** When false, field is table/list only (not shown in the drawer form). Default true. */
  form?: boolean;
}

export interface ResourceListArgs {
  page: number;
  pageLimit: number;
  search?: string;
}

export interface ResourceListResult<T> {
  data: PaginatedResult<T> | undefined;
  isLoading: boolean;
  isFetching?: boolean;
  isError?: boolean;
  refetch?: () => void;
}

export interface ResourceCrudPageProps<T extends ContactRow> {
  title: string;
  description?: string;
  /** Singular noun used in button labels and drawer titles, e.g. "warehouse". */
  entityLabel: string;
  /** Plural noun used in error / empty copy, e.g. "warehouses". */
  entityLabelPlural?: string;
  fields: ResourceCrudField<T>[];
  /** RTK Query list hook, already narrowed to the paginated payload. */
  useList: (args: ResourceListArgs) => ResourceListResult<T>;
  onSave: (values: Record<string, string>, editing: T | null) => Promise<unknown>;
  onDelete?: (row: T) => Promise<unknown>;
  saving?: boolean;
  /** Master switch for create/edit/delete (overridden by canCreate/canUpdate/canDelete). */
  canMutate?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  /** Extra content rendered under the mobile list row subtitle line. */
  subtitleOf?: (row: T) => string | undefined;
  /** Accessible label for the list refresh control. */
  refreshLabel?: string;
}

function isPhoneField<T extends ContactRow>(field: ResourceCrudField<T>): boolean {
  return field.type === 'phone' || field.key === 'phone';
}

function emptyDraft<T extends ContactRow>(fields: ResourceCrudField<T>[]) {
  const draft = Object.fromEntries(fields.map((f) => [f.key, ''])) as Record<string, string>;
  if (fields.some(isPhoneField)) {
    draft.phoneCountry = detectDefaultPhoneCountry();
  }
  return draft;
}

function draftFromRow<T extends ContactRow>(fields: ResourceCrudField<T>[], row: T) {
  const draft = Object.fromEntries(
    fields.map((f) => [
      f.key,
      f.fromRow ? f.fromRow(row) : String(row[f.key] ?? ''),
    ]),
  ) as Record<string, string>;
  const phoneField = fields.find(isPhoneField);
  if (phoneField) {
    const parsed = parseStoredPhone(draft[phoneField.key] ?? '');
    draft[phoneField.key] = parsed.phone;
    draft.phoneCountry = parsed.country;
  }
  return draft;
}

/**
 * List + drawer-form CRUD screen for flat contact-style entities.
 * Pages supply only their RTK hooks and field metadata.
 */
export function ResourceCrudPage<T extends ContactRow>({
  title,
  description,
  entityLabel,
  entityLabelPlural,
  fields,
  useList,
  onSave,
  onDelete,
  saving,
  canMutate = true,
  canCreate,
  canUpdate,
  canDelete,
  subtitleOf,
  refreshLabel = 'Refresh',
}: ResourceCrudPageProps<T>) {
  const allowCreate = canCreate ?? canMutate;
  const allowUpdate = canUpdate ?? canMutate;
  const allowDelete = (canDelete ?? canMutate) && Boolean(onDelete);
  const plural = entityLabelPlural ?? `${entityLabel}s`;
  const [resourceQuery, setResourceQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: DEFAULT_PAGE_LIMIT,
    search: '',
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [form, setForm] = useState<Record<string, string>>(() => emptyDraft(fields));
  const [confirmDelete, setConfirmDelete] = useState<T | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const { data, isLoading, isFetching, isError, refetch } = useList({
    page: resourceQuery.page,
    pageLimit: resourceQuery.pageLimit,
    search: resourceQuery.search || undefined,
  });

  const phoneField = useMemo(() => fields.find(isPhoneField), [fields]);

  function openCreate() {
    setEditing(null);
    setForm(emptyDraft(fields));
    setPhoneError(null);
    setDrawerOpen(true);
  }

  function openEdit(row: T) {
    setEditing(row);
    setForm(draftFromRow(fields, row));
    setPhoneError(null);
    setDrawerOpen(true);
  }

  async function handleSave() {
    if (phoneField) {
      const national = (form[phoneField.key] ?? '').trim();
      const country = (form.phoneCountry || detectDefaultPhoneCountry()) as CountryCode;
      if (phoneField.required || national) {
        if (!isValidNationalPhone(national, country)) {
          setPhoneError('Enter a valid phone number for the selected country');
          return;
        }
      }
    }
    setPhoneError(null);
    const payload = { ...form };
    delete payload.phoneCountry;
    await onSave(payload, editing);
    setDrawerOpen(false);
  }

  async function handleDelete() {
    if (!confirmDelete || !onDelete) return;
    await onDelete(confirmDelete);
    setConfirmDelete(null);
    setDrawerOpen(false);
  }

  const requiredMissing = fields.some((f) => {
    if (!f.required) return false;
    if (isPhoneField(f)) {
      const national = (form[f.key] ?? '').trim();
      const country = form.phoneCountry || detectDefaultPhoneCountry();
      return !isValidNationalPhone(national, country);
    }
    return !(form[f.key] ?? '').trim();
  });

  const defaultSubtitle = (row: T) =>
    fields
      .filter((f) => f.key !== 'name')
      .map((f) => String(row[f.key] ?? '').trim())
      .filter(Boolean)
      .join(' · ');

  return (
    <Stack gap="md">
      <PageHeader title={title} description={description} />
      {isError ? <Alert color="red" title={`Failed to load ${plural}`} /> : null}
      <ResourceLayout
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setResourceQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder={`Search ${plural}...`}
        emptyTitle={`No ${plural} yet`}
        emptyDescription={`${plural[0].toUpperCase()}${plural.slice(1)} for this organization will show up here.`}
        mobileView="list"
        onRefresh={refetch ? () => void refetch() : undefined}
        refreshLabel={refreshLabel}
        addButton={
          allowCreate ? <Button onClick={openCreate}>Add {entityLabel}</Button> : undefined
        }
        onRowClick={allowUpdate ? openEdit : undefined}
        rowActionsHeader="Actions"
        renderRowActions={
          allowUpdate || allowDelete
            ? (row) => (
                <RowActions
                  onEdit={allowUpdate ? () => openEdit(row) : undefined}
                  onDelete={allowDelete ? () => setConfirmDelete(row) : undefined}
                />
              )
            : undefined
        }
        columns={fields
          .filter((f) => f.column !== false)
          .map((f) => ({
            key: f.key,
            header: f.label,
            render: (row: T) => {
              if (f.options?.length) {
                const match = f.options.find((o) => o.value === String(row[f.key] ?? ''));
                return match?.label ?? (String(row[f.key] ?? '') || '—');
              }
              return String(row[f.key] ?? '') || '—';
            },
          }))}
        renderCard={(row: T) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap={4}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Text fw={600}>{row.name}</Text>
                  <Text size="sm" c="dimmed">
                    {(subtitleOf?.(row) ?? defaultSubtitle(row)) || 'No contact'}
                  </Text>
                </Stack>
                {allowUpdate || allowDelete ? (
                  <RowActions
                    onEdit={allowUpdate ? () => openEdit(row) : undefined}
                    onDelete={allowDelete ? () => setConfirmDelete(row) : undefined}
                  />
                ) : null}
              </Group>
            </Stack>
          </Paper>
        )}
        renderListItem={(row: T) => (
          <MobileListRow
            title={row.name}
            subtitle={(subtitleOf?.(row) ?? defaultSubtitle(row)) || undefined}
            onClick={allowUpdate ? () => openEdit(row) : undefined}
            trailing={
              allowUpdate || allowDelete ? (
                <RowActions
                  onEdit={allowUpdate ? () => openEdit(row) : undefined}
                  onDelete={allowDelete ? () => setConfirmDelete(row) : undefined}
                />
              ) : undefined
            }
            showChevron={!allowUpdate}
          />
        )}
      />
      <BottomDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={`${editing ? 'Edit' : 'Add'} ${entityLabel}`}
      >
        <Stack gap="md">
          {fields.filter((field) => field.form !== false).map((field) => (
            <FormField key={field.key} label={field.label} required={field.required}>
              {isPhoneField(field) ? (
                <PhoneInput
                  value={form[field.key] ?? ''}
                  country={form.phoneCountry || detectDefaultPhoneCountry()}
                  error={phoneError ?? undefined}
                  countrySearchPlaceholder="Search country"
                  onChange={({ phone, country }) => {
                    setPhoneError(null);
                    setForm((prev) => ({
                      ...prev,
                      [field.key]: phone,
                      phoneCountry: country,
                    }));
                  }}
                />
              ) : field.options ? (
                <Select
                  data={field.options}
                  value={form[field.key] || null}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, [field.key]: value ?? '' }))
                  }
                  searchable
                  clearable={!field.required}
                />
              ) : (
                <TextInput
                  value={form[field.key] ?? ''}
                  onChange={(e) => {
                    const value = e.currentTarget.value;
                    setForm((prev) => ({ ...prev, [field.key]: value }));
                  }}
                />
              )}
            </FormField>
          ))}
          <Group justify="space-between">
            {editing && allowDelete ? (
              <Button
                color="red"
                variant="light"
                onClick={() => setConfirmDelete(editing)}
              >
                Delete
              </Button>
            ) : (
              <span />
            )}
            <Button
              loading={saving}
              disabled={requiredMissing}
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
      <ConfirmDialog
        opened={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => void handleDelete()}
        title={`Delete ${entityLabel}?`}
        message={`${confirmDelete?.name ?? ''} will be permanently removed.`}
        confirmLabel="Delete"
        danger
      />
    </Stack>
  );
}
